import {
  DEBT_KINDS,
  ScheduleError,
  buildSchedule,
  fromMinor,
  toMinor,
  type DebtKind,
  type DebtScheduleType,
  type DebtsSummaryDto,
} from "@home/shared";
import { useMemo, useState } from "react";
import { parseAmount } from "../amount.ts";
import { fundableAccounts } from "../accounts.ts";
import { api } from "../api.ts";
import { dayTitle, money, todayIso } from "../format.ts";
import { t, tryT, type Lang } from "../i18n.ts";
import { errorText, useMeta } from "../meta.ts";
import { confirmDialog, hapticSelect, hapticTap } from "../telegram.ts";
import { invalidateRemote, useRemote } from "../useRemote.ts";
import { AmountField, type Currency } from "./AmountField.tsx";
import { Segmented } from "./Segmented.tsx";
import { PrimaryAction, Sheet } from "./Sheet.tsx";
import { toast } from "./Toast.tsx";

export const DEBT_ICONS: Record<DebtKind, string> = { credit: "🏦", microloan: "⚡", installment: "🛍", card: "💳", personal: "🤝" };

const TERMS = [3, 6, 12, 24, 36];

function loadLevel(load: number): "ok" | "high" | "danger" {
  return load < 0.3 ? "ok" : load < 0.5 ? "high" : "danger";
}

const fullDate = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

/** Сводка по долгам: остаток, платежи в месяц, нагрузка на доход, дата «без долгов». */
export function DebtsSummaryCard({ s, lang, onOpen }: { s: DebtsSummaryDto; lang: Lang; onOpen?: () => void }) {
  const level = s.load !== null ? loadLevel(s.load) : null;
  return (
    <section className="dash-card">
      <div className="dash-card-head">
        <h3>🏦 {t("debtsTitle")}</h3>
        {onOpen && (
          <button type="button" className="btn-small-ghost" onClick={onOpen}>
            {t("manage")} ›
          </button>
        )}
      </div>
      <div className="stat-row">
        <div className="stat">
          <div className="stat-label">{t("remainingL")}</div>
          <div className="stat-value">{money(s.remainingUzs, "UZS", lang)}</div>
        </div>
        <div className="stat">
          <div className="stat-label">{t("monthlyL")}</div>
          <div className="stat-value">{money(s.monthlyUzs, "UZS", lang)}</div>
        </div>
      </div>
      {s.load !== null && level && (
        <div className={`debt-load load-${level}`}>
          {t("loadL", { pct: Math.round(s.load * 100) })} · {t(`load.${level}`)}
        </div>
      )}
      {s.overdueCount > 0 && <div className="debt-warning">⚠️ {t("overdueN", { n: s.overdueCount })}</div>}
      {s.nextPayment && (
        <div className="hint debt-line">
          {t("nextPaymentL")}: <b>{s.nextPayment.debtName}</b> — {money(s.nextPayment.amount, s.nextPayment.currency, lang)}, {dayTitle(s.nextPayment.dueDate, lang)}
        </div>
      )}
      {s.debtFreeDate && <div className="hint debt-line">🎯 {t("debtFree", { date: fullDate(s.debtFreeDate) })}</div>}
      {s.fxMissing && <div className="hint debt-line">{t("fxMissingDebts")}</div>}
      {s.hiddenPersonalRemainingUzs > 0 && (
        <div className="hint debt-line">{t("personalDebtsOthers", { amount: money(s.hiddenPersonalRemainingUzs, "UZS", lang) })}</div>
      )}
    </section>
  );
}

function defaultsFor(kind: DebtKind): { scheduleType: DebtScheduleType; disbursed: boolean; rate: string } {
  switch (kind) {
    case "installment":
      return { scheduleType: "equal", disbursed: false, rate: "0" };
    case "card":
      return { scheduleType: "fixed", disbursed: false, rate: "" };
    case "personal":
      return { scheduleType: "equal", disbursed: true, rate: "0" };
    default:
      return { scheduleType: "annuity", disbursed: true, rate: "" };
  }
}

export interface DebtPreset {
  name: string;
  monthlyPayment: number;
  paymentDay: number;
  currency: Currency;
}

/** Новый долг. Платёж, срок и переплата считаются сразу, до сохранения. */
export function NewDebtSheet({ lang, onClose, preset, onCreated }: { lang: Lang; onClose: () => void; preset?: DebtPreset; onCreated?: () => void }) {
  const meta = useMeta();
  const today = todayIso();
  const [kind, setKind] = useState<DebtKind>(preset ? "installment" : "credit");
  const initial = preset ? { scheduleType: "fixed" as const, disbursed: false, rate: "0" } : defaultsFor("credit");
  const [name, setName] = useState(preset?.name ?? "");
  const [lender, setLender] = useState("");
  const [principal, setPrincipal] = useState("");
  const [currency, setCurrency] = useState<Currency>(preset?.currency ?? "UZS");
  const [rate, setRate] = useState(initial.rate);
  const [scheduleType, setScheduleType] = useState<DebtScheduleType>(initial.scheduleType);
  const [term, setTerm] = useState<number | null>(12);
  const [termText, setTermText] = useState("");
  const [payment, setPayment] = useState(preset ? String(preset.monthlyPayment) : "");
  const [startDate, setStartDate] = useState(today);
  const [paymentDay, setPaymentDay] = useState(String(preset?.paymentDay ?? Number(today.slice(8, 10))));
  const [disbursed, setDisbursed] = useState(initial.disbursed);
  const [personal, setPersonal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // «Деньги пришли на счёт» — только на счёт, который уже вели, когда взяли кредит: деньги кредита,
  // взятого раньше, давно на руках или потрачены, и остаток бы показал лишние миллионы.
  const accounts = fundableAccounts(meta.data?.accounts ?? [], currency, startDate);
  const [accountId, setAccountId] = useState<string | null>(null);
  const account = accounts.find((a) => a.id === accountId) ?? accounts[0] ?? null;
  const recent = account !== null;
  const addsToBalance = disbursed && recent;

  const changeKind = (next: DebtKind) => {
    const d = defaultsFor(next);
    setKind(next);
    setScheduleType(d.scheduleType);
    setDisbursed(d.disbursed);
    setRate(d.rate);
  };

  const day = Number(paymentDay);
  const rateNum = rate.trim() === "" ? 0 : Number(rate.replace(",", "."));
  const principalNum = parseAmount(principal);
  const paymentNum = parseAmount(payment);

  // Предварительный расчёт тем же кодом, что и на сервере.
  const preview = useMemo(() => {
    if (!principalNum || !(day >= 1 && day <= 31) || !Number.isFinite(rateNum)) return null;
    try {
      const rows = buildSchedule({
        balanceMinor: toMinor(principalNum, currency),
        annualRate: rateNum,
        type: scheduleType,
        startDate,
        firstIndex: 1,
        paymentDay: day,
        ...(scheduleType === "fixed" ? { paymentMinor: paymentNum ? toMinor(paymentNum, currency) : 0n } : { count: term ?? 0 }),
        roundTo: currency === "UZS" ? 100n : 1n,
      });
      const first = rows[0];
      if (!first) return null;
      return {
        payment: fromMinor(first.principalMinor + first.interestMinor, currency),
        count: rows.length,
        interest: fromMinor(
          rows.reduce((s, r) => s + r.interestMinor, 0n),
          currency,
        ),
        past: rows.filter((r) => r.dueDate < today).length,
      };
    } catch (err) {
      return err instanceof ScheduleError ? { error: tryT(err.code) ?? t("err.bad_args") } : null;
    }
  }, [principalNum, paymentNum, currency, rateNum, scheduleType, startDate, day, term, today]);

  const submit = async () => {
    if (busy) return;
    if (!name.trim()) return setError(t("fillName"));
    if (!principalNum) return setError(t("fillAmount"));
    if (scheduleType === "fixed" ? !paymentNum : !term) return setError(scheduleType === "fixed" ? t("fillAmount") : t("termMonths"));
    if (preview && "error" in preview) return setError(preview.error ?? null);
    setBusy(true);
    setError(null);
    try {
      await api.createDebt({
        kind,
        name: name.trim(),
        lender: lender.trim() || null,
        principal: principalNum,
        currency,
        annualRate: scheduleType === "equal" ? 0 : rateNum,
        scheduleType,
        startDate,
        termMonths: scheduleType === "fixed" ? null : term,
        monthlyPayment: scheduleType === "fixed" ? paymentNum : null,
        paymentDay: day,
        personal,
        disbursedAccountId: addsToBalance && account ? account.id : null,
      });
      hapticTap();
      toast(t("savedOk"));
      invalidateRemote();
      onCreated?.();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Sheet title={t("newDebt")} onClose={onClose}>
      <div className="form">
        <div className="chips" role="radiogroup" aria-label={t("type")}>
          {DEBT_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={kind === k}
              className={kind === k ? "chip active" : "chip"}
              onClick={() => {
                hapticSelect();
                changeKind(k);
              }}
            >
              {DEBT_ICONS[k]} {t(`debtKind.${k}`)}
            </button>
          ))}
        </div>
        <label className="field">
          <span>{t("name")}</span>
          <input className="input" placeholder={t("debtNamePh")} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
        </label>
        <AmountField value={principal} onChange={setPrincipal} currency={currency} onCurrency={setCurrency} lang={lang} label={t("debtPrincipal")} />

        <div className="field">
          <span>{t("scheduleKind")}</span>
          <Segmented
            value={scheduleType}
            onChange={setScheduleType}
            label={t("scheduleKind")}
            options={(["annuity", "differentiated", "equal", "fixed"] as const).map((v) => ({ value: v, label: t(`sched.${v}`) }))}
          />
        </div>

        {scheduleType !== "equal" && (
          <label className="field">
            <span>{t("rate")}</span>
            <input className="input" inputMode="decimal" placeholder="24" value={rate} onChange={(e) => setRate(e.target.value.replace(/[^\d.,]/g, ""))} />
          </label>
        )}

        {scheduleType === "fixed" ? (
          <AmountField value={payment} onChange={setPayment} currency={currency} lang={lang} label={t("monthlyPayment")} />
        ) : (
          <div className="field">
            <span>{t("termMonths")}</span>
            <div className="chips" role="radiogroup" aria-label={t("termMonths")}>
              {TERMS.map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={term === m && !termText}
                  className={term === m && !termText ? "chip active" : "chip"}
                  onClick={() => {
                    hapticSelect();
                    setTerm(m);
                    setTermText("");
                  }}
                >
                  {m}
                </button>
              ))}
              <input
                className="input day-input"
                inputMode="numeric"
                placeholder="…"
                value={termText}
                aria-label={t("termMonths")}
                onChange={(e) => {
                  const text = e.target.value.replace(/\D/g, "").slice(0, 3);
                  setTermText(text);
                  setTerm(Number(text) >= 1 ? Number(text) : null);
                }}
              />
            </div>
          </div>
        )}

        <div className="field-row">
          <label className="field">
            <span>{t("startDate")}</span>
            <input className="input" type="date" value={startDate} max={today} onChange={(e) => e.target.value && setStartDate(e.target.value)} />
          </label>
          <label className="field">
            <span>{t("paymentDay")}</span>
            <input className="input" inputMode="numeric" value={paymentDay} onChange={(e) => setPaymentDay(e.target.value.replace(/\D/g, "").slice(0, 2))} />
          </label>
        </div>

        <label className="field">
          <span>{t("lender")}</span>
          <input className="input" value={lender} maxLength={60} onChange={(e) => setLender(e.target.value)} />
        </label>

        {recent && (
          <label className="toggle">
            <input type="checkbox" checked={disbursed} onChange={(e) => setDisbursed(e.target.checked)} />
            <span>{t("disbursed")}</span>
          </label>
        )}
        {addsToBalance && (
          <>
            {accounts.length > 1 && (
              <div className="chips" role="radiogroup">
                {accounts.map((a) => (
                  <button key={a.id} type="button" className={account?.id === a.id ? "chip active" : "chip"} onClick={() => setAccountId(a.id)}>
                    {a.name}
                  </button>
                ))}
              </div>
            )}
            <p className="hint">{t("disbursedHint")}</p>
          </>
        )}
        <label className="toggle">
          <input type="checkbox" checked={personal} onChange={(e) => setPersonal(e.target.checked)} />
          <span>{t("personalDebt")}</span>
        </label>

        {preview && "error" in preview ? (
          <p className="form-error">{preview.error}</p>
        ) : (
          preview && (
            <div className="debt-preview">
              <div>{t("preview", { payment: money(preview.payment, currency, lang), count: preview.count, interest: money(preview.interest, currency, lang) })}</div>
              {preview.past > 0 && <div className="hint">{t("previewPast", { n: preview.past })}</div>}
            </div>
          )
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <PrimaryAction label={t("save")} onClick={() => void submit()} busy={busy} />
      </div>
    </Sheet>
  );
}

/** Долг: остаток, ближайший платёж, оплата, досрочное погашение, график. */
export function DebtDetailSheet({ id, lang, onClose }: { id: string; lang: Lang; onClose: () => void }) {
  const remote = useRemote(`debt:${id}`, () => api.debt(id));
  const d = remote.data;
  const [mode, setMode] = useState<"none" | "other" | "extra">("none");
  const [amount, setAmount] = useState("");
  const [extraMode, setExtraMode] = useState<"term" | "payment">("term");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const done = (closed: boolean) => {
    hapticTap();
    toast(closed ? t("debtClosed") : t("savedOk"));
    invalidateRemote();
    setMode("none");
    setAmount("");
  };

  const pay = async () => {
    if (busy || !d) return;
    const value = mode === "other" ? parseAmount(amount) : null;
    if (mode === "other" && !value) return setError(t("fillAmount"));
    setBusy(true);
    setError(null);
    try {
      done((await api.payDebt(d.id, value)).closed);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const extra = async () => {
    if (busy || !d) return;
    const value = parseAmount(amount);
    if (!value) return setError(t("fillAmount"));
    setBusy(true);
    setError(null);
    try {
      done((await api.extraDebt(d.id, value, extraMode)).closed);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!d || !(await confirmDialog(t("confirmDeleteDebt", { name: d.name }), t("delete")))) return;
    try {
      await api.deleteDebt(d.id);
      toast(t("deletedOk"));
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
    }
  };

  if (!d) {
    return (
      <Sheet title={t("debtsTitle")} onClose={onClose}>
        {remote.loading ? <div className="skeleton skeleton-block" /> : <p className="empty">{t("loadError")}</p>}
      </Sheet>
    );
  }

  const paidPct = d.principal > 0 ? Math.min(100, Math.round(((d.principal - d.remaining) / d.principal) * 100)) : 0;
  const paidCount = d.paymentsTotal - d.paymentsLeft;
  const primaryLabel =
    mode === "extra" ? t("extraPay") : mode === "other" ? t("payNext", { amount: amount ? money(parseAmount(amount) ?? 0, d.currency, lang) : "…" }) : d.nextPayment ? t("payNext", { amount: money(d.nextPayment.amount, d.currency, lang) }) : null;

  return (
    <Sheet title={`${DEBT_ICONS[d.kind]} ${d.name}`} onClose={onClose}>
      <div className="form">
        <div className="goal-summary">
          <div className="goal-line">
            <span className="muted">{t("remainingL")}</span>
            <b>{money(d.remaining, d.currency, lang)}</b>
          </div>
          <span className="progress goal-progress">
            <span style={{ width: `${paidPct}%` }} />
          </span>
          <div className="goal-meta muted">
            {t("paidOf", { paid: paidCount, total: d.paymentsTotal })}
            {d.endDate && ` · ${t("endDateL", { date: fullDate(d.endDate) })}`}
          </div>
          {d.interestLeft > 0 && <div className="goal-meta muted">{t("interestLeft", { amount: money(d.interestLeft, d.currency, lang) })}</div>}
          <div className="goal-meta muted">
            {t(`debtKind.${d.kind}`)}
            {d.lender ? ` · ${d.lender}` : ""}
            {d.annualRate > 0 ? ` · ${d.annualRate}%` : ""}
            {d.personal ? " · 🔒" : ""}
          </div>
        </div>

        {d.status === "active" && (
          <>
            <Segmented
              value={mode}
              onChange={(m) => {
                setMode(m);
                setAmount("");
                setError(null);
              }}
              options={[
                { value: "none", label: t("nextPaymentL") },
                { value: "other", label: t("payOther") },
                { value: "extra", label: t("extraPay") },
              ]}
            />
            {mode === "none" && d.nextPayment && (
              <div className={d.nextPayment.overdue ? "debt-next overdue" : "debt-next"}>
                {dayTitle(d.nextPayment.dueDate, lang)} — <b>{money(d.nextPayment.amount, d.currency, lang)}</b>
                {d.nextPayment.overdue && ` · ⚠️ ${t("overdueL")}`}
              </div>
            )}
            {mode !== "none" && <AmountField value={amount} onChange={setAmount} currency={d.currency} lang={lang} />}
            {mode === "extra" && (
              <>
                <Segmented
                  value={extraMode}
                  onChange={setExtraMode}
                  options={[
                    { value: "term", label: t("extraTerm") },
                    { value: "payment", label: t("extraPayment") },
                  ]}
                />
                <p className="hint">{t("extraHint")}</p>
              </>
            )}
          </>
        )}

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        <h3 className="form-title">{t("scheduleTitle")}</h3>
        <ul className="row-list schedule">
          {d.payments.map((p) => (
            <li key={p.id} className={`row schedule-row${p.status === "paid" ? " paid" : ""}${p.overdue ? " overdue" : ""}`}>
              <span className="row-icon" aria-hidden="true">
                {p.status === "paid" ? "✅" : p.overdue ? "⚠️" : p.kind === "extra" ? "⚡" : "🗓"}
              </span>
              <div className="row-body">
                <div className="row-title">{fullDate(p.dueDate)}</div>
                <div className="row-meta">
                  {p.kind === "extra" ? t("extraPay") : p.interest > 0 ? `${money(p.principal, d.currency, lang)} + % ${money(p.interest, d.currency, lang)}` : ""}
                  {p.historic ? ` · ${t("historicPaid")}` : ""}
                </div>
              </div>
              <div className="row-end amount">{money(p.amount, d.currency, lang)}</div>
            </li>
          ))}
        </ul>

        <button type="button" className="btn danger btn-block" onClick={() => void remove()}>
          🗑 {t("deleteDebt")}
        </button>
        {d.status === "active" && primaryLabel && (
          <PrimaryAction label={primaryLabel} onClick={() => void (mode === "extra" ? extra() : pay())} busy={busy} />
        )}
      </div>
    </Sheet>
  );
}

/** Список долгов с прогрессом. */
export function useDebts() {
  return useRemote("debts", () => api.debts().then((r) => r.debts));
}
